import { Box, Clipboard, Icon, Skeleton, Tag, Tooltip } from '@chakra-ui/react'
import { Check, Copy } from 'lucide-react'
import type React from 'react'
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type DataTagProps = {
  value?: string
  externalLink?: string
  maxWidthPx?: number
  onClickBody?: () => void
  hasCopy?: boolean
} & React.ComponentProps<typeof Tag.Root>

export function DataTag({
  /**
   * The value that the tag represents. The visual value and this value that is used in the potential copy action are independent.
   */
  value,
  /**
   * Link to route to external resources from this tag. Only affects the clickable text portion of the tag so the copy button doesn't get overrriden.
   *
   */
  externalLink,
  /**
   * Max width in px that can optionally pass to shorten the visual value of the tag.
   */
  maxWidthPx,
  /**
   * Only works if no external link is given, don't pass in both. Function to be called when the text body of the tag is clicked.
   */
  onClickBody,
  /**
   * By default shows a copy button, pass false to remove it.
   */
  hasCopy = true,
  children,
  ...tag
}: PropsWithChildren<DataTagProps>) {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const [hasCopied, setHasCopied] = useState(false)

  const [isLoading, setIsLoading] = useState(
    value === undefined || value === null || value === '',
  )
  const isEmpty = useMemo(
    () => value === undefined || value === null || value === '',
    [value],
  )
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false)

  useEffect(() => {
    if (externalLink) {
      setIsLoading(false)
    }
    if (!isLoading) {
      return
    }
    const timeout = setTimeout(() => setIsLoading(false), 1500)
    return () => clearTimeout(timeout)
  }, [isLoading, externalLink])

  const valueIsClickable = !!externalLink || onClickBody

  function handleRouting() {
    if (externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer')
    } else if (onClickBody) {
      onClickBody()
    }
  }

  const isNotExternalLink = isEmpty && !externalLink

  const style = {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: maxWidthPx ? `${maxWidthPx}px` : 'inherit',
    display: 'block',
  }

  const textHover = valueIsClickable
    ? {
        cursor: 'pointer',
        textDecoration: 'underline',
      }
    : {
        cursor: 'default',
      }

  const tagColorPalette = useMemo(() => {
    if (tag.colorPalette) {
      return tag.colorPalette
    }
    if (!isLoading && isNotExternalLink) {
      return 'red'
    }
    if (valueIsClickable) {
      return 'blue'
    }
    return tag.colorPalette || 'gray'
  }, [isLoading, tag.colorPalette, isNotExternalLink, valueIsClickable])

  return (
    <Skeleton loading={isLoading}>
      <Tag.Root
        {...tag}
        style={{
          cursor: 'default',
        }}
        _hover={{
          cursor: 'pointer',
        }}
        colorPalette={tagColorPalette}
        variant={tag.variant}
      >
        {isLoading && <Tag.Label>Loading...</Tag.Label>}

        {isEmpty && isNotExternalLink ? (
          <Tag.Label>{t('general.notAvailable')}</Tag.Label>
        ) : (
          <Box
            _hover={textHover}
            onClick={handleRouting}
            style={style}
            className="data-tag-text"
          >
            <Tag.Label>{children}</Tag.Label>
          </Box>
        )}

        {hasCopy && !isLoading && !isEmpty && (
          <Clipboard.Root
            value={value ?? ''}
            onStatusChange={(details) => {
              if (details.copied) {
                setHasCopied(true)
                setTooltipIsOpen(true)
                setTimeout(() => {
                  setTooltipIsOpen(false)
                  setHasCopied(false)
                }, 1500)
              }
            }}
          >
            <Tooltip.Root open={tooltipIsOpen}>
              <Tooltip.Trigger asChild>
                <Clipboard.Trigger
                  aria-label={
                    hasCopied
                      ? t('copiedToClipboard') || 'Copied to clipboard'
                      : t('copyToClipboard') || 'Copy to clipboard'
                  }
                >
                  <Icon
                    ml={2}
                    p={1}
                    as={hasCopied ? Check : Copy}
                    color={hasCopied ? 'icon.icon-success' : 'inherit'}
                    _hover={{
                      cursor: 'pointer',
                    }}
                  />
                </Clipboard.Trigger>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content>
                  <Tooltip.Arrow />
                  Copied
                </Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          </Clipboard.Root>
        )}
      </Tag.Root>
    </Skeleton>
  )
}
